import {DischargeMethod} from '@hconnect/tuc-processes/fields/unloadingMethod/types'
import {useUnloadingMethod} from '@hconnect/tuc-processes/fields/unloadingMethod/useUnloadingMethod'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {Typography, Box, FormControl, Select, MenuItem, SelectChangeEvent} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {CanLargerPumpFitInput} from './CanLargerPumpFitInput'
import {DischargeMethodSelection} from './DischargeMethodSelection'
import {HoseLengthInput} from './HoseLengthInput'
import {IsPossibleToCleanPumpOnSiteInput} from './IsPossibleToCleanPumpOnSiteInput'

/**
 * Comprehensive pump configuration component matching React Native behavior
 * Includes pump size selection, discharge method, and advanced options
 */
export const PumpConfigurationOptions: React.FC = () => {
  const {t} = useTranslation()
  const {
    selectedPump,
    availablePumpTypes,
    setPumpType,
    setPumpSettings,
    setPumpDischarge,
    configuration: {pump: pumpSettings}
  } = useUnloadingMethod()

  if (!pumpSettings.isEnabled || !selectedPump?.pumpSettings) {
    return null
  }

  const handlePumpTypeChange = (event: SelectChangeEvent<string>) => {
    setPumpType(event.target.value)
  }

  const handleDischargeMethodChange = (method: DischargeMethod) => {
    setPumpDischarge({method})
  }

  const handleHoseLengthChange = (hoseLength: number) => {
    setPumpDischarge({method: 'hose', hoseLength})
  }

  const handleCanLargerPumpFit = (canLargerPumpFit: boolean) => {
    setPumpSettings({canLargerPumpFit})
  }

  const handleIsPossibleToCleanPumpOnSite = (isPossibleToCleanPumpOnSite: boolean) => {
    setPumpSettings({isPossibleToCleanPumpOnSite})
  }

  return (
    <Box sx={{width: '100%', mt: 3}}>
      {/* Pump Size Selection */}
      <Box sx={{mb: 3}}>
        <Typography
          sx={{
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '16px',
            color: '#52667a',
            marginBottom: '8px'
          }}
        >
          {t('orderIntake.rmc.steps.unloading.pumpConfiguration.pumpSize', 'Pump size')}
        </Typography>
        <FormControl fullWidth>
          <Select
            value={selectedPump.pumpType?.number}
            onChange={handlePumpTypeChange}
            displayEmpty
            sx={{
              fontFamily: 'Inter',
              fontSize: '14px',
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #d1d9e0'
              },
              '& .MuiSelect-select': {
                padding: '12px 14px'
              }
            }}
            IconComponent={ExpandMoreIcon}
            data-test-id="pump-size-select"
          >
            {availablePumpTypes.map((pump) => (
              <MenuItem key={pump.number} value={pump.number}>
                {pump.size} {pump.sizeUom}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Toggle Switches */}
      <Box sx={{mb: 3}}>
        <CanLargerPumpFitInput
          checked={selectedPump?.pumpSettings?.canLargerPumpFit ?? false}
          onChange={handleCanLargerPumpFit}
        />

        <IsPossibleToCleanPumpOnSiteInput
          checked={selectedPump.pumpSettings?.isPossibleToCleanPumpOnSite ?? false}
          onChange={handleIsPossibleToCleanPumpOnSite}
          marginBottom={0}
        />
      </Box>

      {/* Discharge Method Selection */}
      <DischargeMethodSelection
        value={selectedPump.pumpSettings.discharge.method}
        onChange={handleDischargeMethodChange}
      />

      {/* Hose Length Input (only when hose is selected) */}
      {selectedPump.pumpSettings?.discharge.method === 'hose' && (
        <HoseLengthInput
          discharge={selectedPump.pumpSettings.discharge}
          hoseLengthConfig={pumpSettings.hoseLength}
          onHoseLengthChange={handleHoseLengthChange}
        />
      )}
    </Box>
  )
}
